import React, { useState, useEffect } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import {
  ACCENT, ACCENT_BG, BORDER_SUBTLE, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_FAINT,
  NEU_BG, NEU_RAISED,
} from '../theme';

function UserForm({ initial, roles, onSave, onCancel }) {
  const [username, setUsername] = useState(initial?.username ?? '');
  const [password, setPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(initial?.is_admin ?? false);
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [roleId, setRoleId] = useState(initial?.role_id ?? 0);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const isEdit = !!initial;

  const handleSave = async () => {
    if (!username.trim()) { setErr('יש להזין שם משתמש'); return; }
    if (!isEdit && !password) { setErr('יש להזין סיסמא'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { username: username.trim(), is_admin: isAdmin };
      if (!isEdit) payload.password = password;
      if (isEdit && password) payload.password = password;
      if (isEdit) payload.is_active = isActive;
      payload.role_id = roleId || null;
      await onSave(payload);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '9px 12px', borderRadius: 8,
    border: `1px solid ${BORDER_SUBTLE}`,
    background: NEU_BG, color: TEXT_PRIMARY,
    fontSize: 14, fontFamily: 'Heebo, sans-serif', direction: 'rtl',
  };

  return (
    <div style={{ background: NEU_RAISED, border: `1px solid ${BORDER_SUBTLE}`, borderRadius: 12, padding: '20px 18px', marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY, marginBottom: 16 }}>
        {isEdit ? `עריכת משתמש: ${initial.username}` : 'משתמש חדש'}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, display: 'block', marginBottom: 5 }}>שם משתמש</label>
          <input value={username} onChange={e => setUsername(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, display: 'block', marginBottom: 5 }}>
            {isEdit ? 'סיסמא חדשה (ריק = ללא שינוי)' : 'סיסמא'}
          </label>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, display: 'block', marginBottom: 5 }}>סוג משתמש</label>
        <select
          value={roleId}
          onChange={e => setRoleId(Number(e.target.value))}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          <option value={0}>ללא סוג</option>
          {roles.map(r => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
          <span style={{ fontSize: 13, color: TEXT_PRIMARY }}>מנהל מערכת</span>
        </label>
        {isEdit && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
            <span style={{ fontSize: 13, color: TEXT_PRIMARY }}>חשבון פעיל</span>
          </label>
        )}
      </div>

      {err && <div style={{ color: 'var(--danger, #ef4444)', fontSize: 13, marginBottom: 10 }}>{err}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleSave} disabled={saving} style={{
          padding: '8px 18px', borderRadius: 8, border: 'none',
          background: ACCENT, color: '#fff',
          fontSize: 13, fontWeight: 600, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
        }}>
          {saving ? 'שומר...' : 'שמור'}
        </button>
        <button onClick={onCancel} style={{
          padding: '8px 14px', borderRadius: 8, border: `1px solid ${BORDER_SUBTLE}`,
          background: 'none', color: TEXT_SECONDARY,
          fontSize: 13, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
        }}>ביטול</button>
      </div>
    </div>
  );
}

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/admin/users'),
      api.get('/admin/user-roles'),
    ])
      .then(([u, r]) => { setUsers(u.data); setRoles(r.data); })
      .catch(() => setError('שגיאה בטעינה'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleCreate = async (data) => {
    await api.post('/admin/users', data);
    setShowCreate(false);
    load();
  };

  const handleUpdate = async (id, data) => {
    await api.put(`/admin/users/${id}`, data);
    setEditId(null);
    load();
  };

  const handleDelete = async (u) => {
    if (u.id === currentUser?.id) { alert('לא ניתן למחוק את עצמך'); return; }
    if (!window.confirm(`למחוק את המשתמש "${u.username}"?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'שגיאה במחיקה');
    }
  };

  if (loading) return <div style={{ color: TEXT_FAINT, padding: 20 }}>טוען...</div>;
  if (error) return <div style={{ color: 'var(--danger, #ef4444)', padding: 20 }}>{error}</div>;

  return (
    <div style={{ direction: 'rtl', fontFamily: 'Heebo, sans-serif', maxWidth: 800 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: TEXT_PRIMARY }}>ניהול משתמשים</div>
          <div style={{ fontSize: 12, color: TEXT_FAINT, marginTop: 2 }}>הוסף, ערוך או הסר משתמשים</div>
        </div>
        {!showCreate && !editId && (
          <button onClick={() => setShowCreate(true)} style={{
            padding: '8px 16px', borderRadius: 8, border: 'none',
            background: ACCENT, color: '#fff',
            fontSize: 13, fontWeight: 600, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
          }}>
            + משתמש חדש
          </button>
        )}
      </div>

      {showCreate && (
        <UserForm roles={roles} onSave={handleCreate} onCancel={() => setShowCreate(false)} />
      )}

      {users.map(u => (
        <div key={u.id}>
          {editId === u.id ? (
            <UserForm
              initial={u}
              roles={roles}
              onSave={data => handleUpdate(u.id, data)}
              onCancel={() => setEditId(null)}
            />
          ) : (
            <div style={{
              background: NEU_RAISED,
              border: `1px solid ${BORDER_SUBTLE}`,
              borderRadius: 12, padding: '12px 16px',
              marginBottom: 8,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              {/* Avatar */}
              <div style={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                background: u.is_admin ? ACCENT : 'var(--surf-alt)',
                color: u.is_admin ? '#fff' : TEXT_FAINT,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700,
              }}>
                {u.username[0].toUpperCase()}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY }}>{u.username}</span>
                  {u.id === currentUser?.id && (
                    <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 5, background: ACCENT_BG, color: ACCENT, fontWeight: 600 }}>אתה</span>
                  )}
                  {u.is_admin && (
                    <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 5, background: 'rgba(239,68,68,0.1)', color: 'var(--danger, #ef4444)', fontWeight: 600 }}>מנהל</span>
                  )}
                  {!u.is_active && (
                    <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 5, background: 'var(--surf-alt)', color: TEXT_FAINT, fontWeight: 600 }}>מושבת</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: TEXT_FAINT }}>
                  {u.role ?? 'ללא סוג משתמש'}
                  {u.created_at && ` · נוצר ${new Date(u.created_at).toLocaleDateString('he-IL')}`}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => { setEditId(u.id); setShowCreate(false); }} style={{
                  padding: '5px 12px', borderRadius: 7, border: `1px solid ${BORDER_SUBTLE}`,
                  background: 'none', color: TEXT_SECONDARY,
                  fontSize: 12, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
                }}>עריכה</button>
                {u.id !== currentUser?.id && (
                  <button onClick={() => handleDelete(u)} style={{
                    padding: '5px 12px', borderRadius: 7, border: '1px solid var(--danger, #ef4444)',
                    background: 'none', color: 'var(--danger, #ef4444)',
                    fontSize: 12, fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
                  }}>מחיקה</button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
